import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import {
  Calendar, CheckSquare, BarChart3, Trophy, Search, Settings, Bell,
  Building, Shield, Mail, MessageCircle, ShieldCheck, FileBarChart2,
  Gift, Wallet, Receipt, Users, UserCheck, ChevronRight, LogOut, Activity, HeartHandshake, Video
} from 'lucide-react';

const SECTION = (title, links) => ({ title, links });

/**
 * Mobile "More" page reachable from the 5th bottom tab. Surfaces secondary
 * navigation that doesn't fit on the bottom bar.
 */
export default function MorePage() {
  const { user, logout } = useAuth();
  const role = user?.role;
  const isAdmin = role === 'admin' || role === 'super_admin';
  const isSuper = role === 'super_admin';

  const sections = [
    SECTION('Productivity', [
      { to: '/appointments', icon: Calendar, label: 'Appointments' },
      { to: '/tasks', icon: CheckSquare, label: 'Tasks' },
      { to: '/find-leads', icon: Search, label: 'Find Leads' },
      { to: '/leaderboard', icon: Trophy, label: 'Leaderboard' },
      { to: '/notifications', icon: Bell, label: 'Notifications' },
    ]),
    SECTION('Account', [
      { to: '/settings', icon: Settings, label: 'Settings' },
      { to: '/billing', icon: Wallet, label: 'Billing' },
    ]),
  ];

  if (isAdmin) {
    sections.push(SECTION('Admin', [
      { to: '/admin/analytics', icon: BarChart3, label: 'Analytics' },
      { to: '/admin/collaboration', icon: HeartHandshake, label: 'Team Collaboration' },
      { to: '/admin/marketing-videos', icon: Video, label: 'Marketing Videos' },
      { to: '/admin/system', icon: Activity, label: 'System Dashboard' },
      { to: '/admin/organizations', icon: Building, label: 'Organizations' },
      { to: '/admin/members-overview', icon: Users, label: 'Member Activity' },
      { to: '/admin/marketing', icon: Mail, label: 'Marketing' },
      { to: '/admin/sms-campaign', icon: MessageCircle, label: 'SMS Campaign' },
      { to: '/admin/sms-drip', icon: MessageCircle, label: 'SMS Drip' },
      { to: '/admin/sms-consent-log', icon: ShieldCheck, label: 'SMS Consent Log' },
    ]));
  }

  if (isSuper) {
    sections.push(SECTION('Super Admin', [
      { to: '/admin/super', icon: Shield, label: 'Super Admin' },
      { to: '/admin/coupons', icon: Gift, label: 'Coupons' },
      { to: '/admin/payments', icon: Receipt, label: 'Payments' },
      { to: '/admin/audit-log', icon: FileBarChart2, label: 'Audit Log' },
      { to: '/admin/lead-requests', icon: UserCheck, label: 'Lead Requests' },
    ]));
  }

  return (
    <div className="max-w-3xl mx-auto px-3 sm:px-6 py-4 pb-24 lg:pb-8" data-testid="more-page">
      <div className="mb-4">
        <p className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-medium">Hi, {user?.name?.split(' ')[0] || 'there'}</p>
        <h1 className="font-heading text-xl font-bold text-slate-900">More options</h1>
      </div>

      {sections.map(s => (
        <section key={s.title} className="mb-5">
          <h2 className="text-[10px] uppercase tracking-[0.15em] text-slate-500 font-medium mb-1.5 px-1">{s.title}</h2>
          <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
            {s.links.map((link, idx) => (
              <Link
                key={link.to}
                to={link.to}
                className={`flex items-center gap-3 px-3 py-3.5 min-h-[52px] hover:bg-slate-50 active:bg-slate-100 transition-colors ${
                  idx > 0 ? 'border-t border-slate-100' : ''
                }`}
                data-testid={`more-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <link.icon className="w-4 h-4 text-slate-500 flex-shrink-0" />
                <span className="flex-1 text-sm text-slate-900">{link.label}</span>
                <ChevronRight className="w-4 h-4 text-slate-300" />
              </Link>
            ))}
          </div>
        </section>
      ))}

      <button
        type="button"
        onClick={() => logout && logout()}
        className="w-full mt-4 px-3 py-3 min-h-[52px] flex items-center justify-center gap-2 text-sm font-medium text-red-700 bg-white border border-red-200 rounded-sm hover:bg-red-50 active:bg-red-100"
        data-testid="more-logout-btn"
      >
        <LogOut className="w-4 h-4" /> Sign out
      </button>
    </div>
  );
}
